import { Page, Card, Text, Select, TextField, InlineStack, BlockStack } from "@shopify/polaris";
import { useEffect, useMemo, useState } from "react";

function parseVariantIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("variantId") ||
      params.get("variant_id") ||
      params.get("variant") ||
      ""
    );
  } catch {
    return "";
  }
}

function toIsoDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function addDays(dateString, days) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Math.max(0, Number(days) - 1));
  return toIsoDate(d);
}

export default function RentalPage({ onNavigate }) {
  const [variantId, setVariantId] = useState(() => parseVariantIdFromUrl());
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [available, setAvailable] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [price, setPrice] = useState("0.00");
  const [frequency, setFrequency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [message, setMessage] = useState("");
  const [rateOptions, setRateOptions] = useState([]);

  const frequencyOptions = useMemo(
    () => [
      { label: "Choose a period", value: "" },
      ...rateOptions.map((o) => ({ label: o.label, value: o.value })),
    ],
    [rateOptions],
  );

  useEffect(() => {
    const loadConfig = async () => {
      if (!variantId) {
        setMessage("Provide a variant ID to load rental information.");
        return;
      }

      setLoadingConfig(true);
      setMessage("");
      try {
        const res = await fetch(`/api/rental-config/${encodeURIComponent(variantId)}`);
        const data = await res.json();

        if (!data?.configured) {
          setRateOptions([]);
          setMessage("No rental configuration available for this variant.");
          return;
        }

        const options = Array.isArray(data?.options) ? data.options : [];
        setRateOptions(options);

        if (!options.length) {
          setMessage("No sub-products or product items available for this rental. Please contact support.");
        }
      } catch (e) {
        setMessage(e?.message || "Failed to load rental configuration.");
      } finally {
        setLoadingConfig(false);
      }
    };

    loadConfig();
  }, [variantId]);

  useEffect(() => {
    if (!startDate || !frequency) return;
    const days = frequency === "daily" ? 1 : frequency === "weekly" ? 7 : 30;
    setEndDate(addDays(startDate, days));
  }, [startDate, frequency]);

  useEffect(() => {
    const fetchPrice = async () => {
      if (!variantId || !startDate || !endDate || !frequency) return;

      setLoadingPrice(true);
      try {
        const res = await fetch("/api/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            variantId,
            startDate,
            endDate,
            rateType: frequency,
          }),
        });

        const data = await res.json();
        if (res.ok && data?.available) {
          setAvailable(true);
          setPrice(String(data.price || "0.00"));
          setCurrency(String(data.currency || "USD"));
          setMessage("");
        } else {
          setAvailable(false);
          setPrice("0.00");
          setMessage(data?.message || "No sub-products or product items available for this rental. Please contact support.");
        }
      } catch (e) {
        setAvailable(false);
        setPrice("0.00");
        setMessage(e?.message || "Unable to calculate rental price right now.");
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchPrice();
  }, [variantId, startDate, endDate, frequency]);

  return (
    <Page
      title="Rental Information"
      backAction={onNavigate ? { content: "Dashboard", onAction: () => onNavigate("dashboard") } : undefined}
    >
      <Card>
        <div
          style={{
            border: "1px solid #d9dce1",
            borderRadius: 14,
            padding: 20,
            maxWidth: 760,
            background: "#fff",
          }}
        >
          <BlockStack gap="400">
            <Text as="h2" variant="headingLg">
              {"\u{1F4C6} Rental Information"}
            </Text>

            <Text as="p" variant="bodyLg">
              Rental Price: {currency === "USD" ? "$" : `${currency} `}{price}
              {(loadingConfig || loadingPrice) ? " (loading...)" : ""}
            </Text>

            <Select
              label="Select Rental Frequency:"
              options={frequencyOptions}
              value={frequency}
              onChange={setFrequency}
            />

            <InlineStack gap="400" align="start" wrap>
              <div style={{ minWidth: 280 }}>
                <TextField
                  label="Start Date:"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  autoComplete="off"
                />
              </div>
              <div style={{ minWidth: 280 }}>
                <TextField
                  label="End Date:"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  autoComplete="off"
                  helpText="Auto-set based on selected frequency"
                />
              </div>
            </InlineStack>

            {!available && (
              <Text as="p" tone="subdued">
                {message || "No sub-products or product items available for this rental. Please contact support."}
              </Text>
            )}

            {!parseVariantIdFromUrl() && (
              <TextField
                label="Variant ID (for testing)"
                value={variantId}
                onChange={setVariantId}
                autoComplete="off"
                helpText="Pass variantId in URL for production usage."
              />
            )}
          </BlockStack>
        </div>
      </Card>
    </Page>
  );
}
